import SelectField from "@/components/common/select";
import type { ExternalUserFormData } from "@/schemas/users/external-user";
import {
  Heading,
  TextField,
  GroupTextField,
  GroupTextFieldRow,
  Input,
} from "@elaniintc/goes-design-system";
import { Controller, useFormContext } from "react-hook-form";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import type { IDropdownListItem } from "@/components/ui/dropdown-list/dropdown-list-item";
import { useState } from "react";
import DeactivateModal from "@/pages/users/details/edit/deactivate-modal";
import ReactivateModal from "@/pages/users/details/edit/reactivate-modal";
import { useCarneMask, useDUIMask, useNITMask } from "@/hooks/use-masks";
import { DateInput } from "@/workflow/components/configurations/form/components/DateInput";
import { InternationalInputPhone } from "@/components/international-input-phone";

interface Props {
  userId?: string;
  userActivationHasChanged: boolean;
  isUserActive: boolean;
  isPreRegistered?: boolean;
  setSelectedUserType: (
    userType: {
      label: string;
      value: string;
    } | null,
  ) => void;
  onSubmit: (data: ExternalUserFormData, description: string) => void;
  setIsUserActive: (value: boolean) => void;
}

const DOCUMENT_TYPES = [
  { value: "DUI", label: "DUI" },
  { value: "PASSPORT", label: "Pasaporte" },
  { value: "CARNE_INSTITUCIONAL", label: "Carné de residente" },
  { value: "NIT", label: "NIT" },
];

const USER_TYPES = [
  { value: "APPLICANT", label: "Solicitante" },
  { value: "FACILITATOR", label: "Tramitador" },
  { value: "REPRESENTATIVE", label: "Representante" },
  { value: "INSTITUTION", label: "Institución" },
];

const getDocumentPlaceholder = (documentType: string) => {
  switch (documentType) {
    case "DUI":
      return "00000000-0";
    case "NIT":
      return "0000-000000-000-0";
    case "PASSPORT":
      return "Número de pasaporte";
    case "CARNE_INSTITUCIONAL":
      return "12345678";
    default:
      return "Número de documento";
  }
};

const getNameLabel = (documentType: string) => {
  switch (documentType) {
    case "DUI":
      return "Nombres según DUI";
    case "NIT":
      return "Razón social";
    case "PASSPORT":
      return "Nombres según pasaporte";
    case "CARNE_INSTITUCIONAL":
      return "Nombres según carné";
    default:
      return "Nombres";
  }
};

const getLastNameLabel = (documentType: string) => {
  switch (documentType) {
    case "DUI":
      return "Apellidos según DUI";
    case "PASSPORT":
      return "Apellidos según pasaporte";
    case "CARNE_INSTITUCIONAL":
      return "Apellidos según carné";
    default:
      return "Apellidos";
  }
};

const ExternalUserForm = ({
  userId,
  userActivationHasChanged,
  isUserActive,
  isPreRegistered = false,
  setSelectedUserType,
  onSubmit,
  setIsUserActive,
}: Props) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    register,
    watch,
    trigger,
  } = useFormContext<ExternalUserFormData>();

  const [document_type, type] = watch(["document_type", "type"]);

  const duiMask = useDUIMask();
  const nitMask = useNITMask();
  const carneMask = useCarneMask();

  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);

  const showLastNameField = document_type !== "NIT";

  const [tempData, setTempData] = useState<ExternalUserFormData | null>(null);
  const handleFormSubmit = (data: ExternalUserFormData) => {
    if (userActivationHasChanged) {
      setTempData(data);
      if (!isUserActive) {
        setIsDeactivateModalOpen(true);
      } else {
        setIsReactivateModalOpen(true);
      }
      return;
    }
    onSubmit(data, "");
  };

  const handleDeactivateAccept = (val: { description: string }) => {
    if (tempData) {
      onSubmit(tempData, val.description);
    }
    setIsDeactivateModalOpen(false);
    setTempData(null);
  };

  const handleReactivateAccept = (val: { description: string }) => {
    const data = watch();
    setIsUserActive(true);
    setIsReactivateModalOpen(false);
    onSubmit(data, val.description);
  };

  return (
    <>
      <DeactivateModal
        userId={userId || null}
        isOpen={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        onAccept={handleDeactivateAccept}
      />
      <ReactivateModal
        isOpen={isReactivateModalOpen}
        onClose={() => setIsReactivateModalOpen(false)}
        onAccept={handleReactivateAccept}
      />
      <form
        className="flex flex-col gap-6 border rounded-lg bg-neutral-object p-6 border-neutral-quiet"
        onSubmit={handleSubmit(handleFormSubmit)}
        id="external-user-form"
      >
        <div className="flex flex-col gap-4 @container">
          <Heading variant="h5">Información personal</Heading>
          <div className="flex flex-col gap-4">
            <Controller
              name="document_number"
              control={control}
              render={({ field: { ref, onChange, value, ...rest } }) => {
                return (
                  <GroupTextField
                    label="Documento de identidad"
                    required
                    error={errors.document_number?.message}
                  >
                    <DropdownList
                      heightClassName="h-35"
                      widthClassName="w-fit pr-10"
                      placeholder="Tipo"
                      group="left"
                      options={DOCUMENT_TYPES.map((dt) => ({
                        value: dt.value,
                        label: dt.label,
                      }))}
                      selectedValue={
                        document_type
                          ? {
                              value: document_type,
                              label:
                                DOCUMENT_TYPES.find(
                                  (dt) => dt.value === document_type,
                                )?.label || "",
                            }
                          : undefined
                      }
                      toggleSelection={(value: IDropdownListItem) => {
                        setValue(
                          "document_type",
                          value.value as
                            "DUI" | "NIT" | "PASSPORT" | "CARNE_INSTITUCIONAL",
                        );
                        trigger("document_type");
                        setValue("document_number", "");
                        if (value.value === "NIT") {
                          setValue("last_name", "");
                          setValue("birthdate", "");
                        }
                      }}
                    />
                    <Input
                      {...rest}
                      key={`document-input-${document_type || "empty"}`}
                      id="document-number"
                      className="w-full"
                      placeholder={getDocumentPlaceholder(document_type)}
                      variant="xs"
                      value={value}
                      onChange={onChange}
                      ref={(element) => {
                        ref(element);
                        if (document_type === "DUI") {
                          duiMask(element);
                        } else if (document_type === "NIT") {
                          nitMask(element);
                        } else if (document_type === "CARNE_INSTITUCIONAL") {
                          carneMask(element);
                        }
                      }}
                    />
                  </GroupTextField>
                );
              }}
            />

            {document_type === "NIT" ? (
              <>
                <TextField
                  id="first-name"
                  label={getNameLabel(document_type)}
                  placeholder="Nombre legal de la institución"
                  required
                  error={errors.first_name?.message}
                  {...register("first_name")}
                />
                <GroupTextField
                  label="Nombre comercial"
                  labelHelperText="Opcional"
                  error={errors.commercial_name?.message}
                >
                  <Input
                    id="commercial-name"
                    placeholder="Nombre comercial de la institución"
                    {...register("commercial_name")}
                  />
                </GroupTextField>
              </>
            ) : (
              <GroupTextField
                label="Información según documento"
                required
                error={
                  errors.first_name?.message ||
                  errors.last_name?.message ||
                  errors.birthdate?.message
                }
              >
                <GroupTextFieldRow>
                  <Controller
                    name="first_name"
                    control={control}
                    render={({ field }) => {
                      return (
                        <Input
                          {...field}
                          id="first-name"
                          className="w-full"
                          placeholder={getNameLabel(document_type)}
                          group="up-left"
                        />
                      );
                    }}
                  />
                  {showLastNameField && (
                    <Controller
                      name="last_name"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Input
                            {...field}
                            id="last-name"
                            className="w-full"
                            placeholder={getLastNameLabel(document_type)}
                            group="up-right"
                          />
                        );
                      }}
                    />
                  )}
                </GroupTextFieldRow>
                <GroupTextFieldRow>
                  <Controller
                    name="birthdate"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      const displayValue = (() => {
                        if (!value) return "";
                        try {
                          const date = new Date(value);
                          const day = String(date.getDate()).padStart(2, "0");
                          const month = String(date.getMonth() + 1).padStart(
                            2,
                            "0",
                          );
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        } catch {
                          return "";
                        }
                      })();

                      return (
                        <DateInput
                          value={displayValue}
                          onChangeDate={onChange}
                          placeholder="Fecha de nacimiento"
                          group="down"
                        />
                      );
                    }}
                  />
                </GroupTextFieldRow>
              </GroupTextField>
            )}
          </div>
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="flex flex-col gap-4 @container">
          <Heading variant="h5">Información de contacto</Heading>
          <div className="flex flex-col gap-4">
            <TextField
              id="email"
              label="Correo electrónico"
              placeholder="nombre@ejemplo.com"
              required={!isPreRegistered}
              error={errors.email?.message}
              {...register("email")}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field: { ref, ...rest } }) => {
                return (
                  <InternationalInputPhone
                    {...rest}
                    inputRef={ref}
                    error={errors.phone?.number?.message}
                  />
                  // <PhoneField
                  //   {...rest}
                  //   id="phone-number"
                  //   formRef={ref}
                  //   label="Número de teléfono"
                  //   placeholder="0000 0000"
                  //   error={errors.phone?.message}
                  //   required
                  // />
                );
              }}
            />
          </div>
        </div>
        <div className="w-full h-px border-t border-neutral-quiet" />
        <div className="flex flex-col gap-4 @container">
          <Heading variant="h5">Configuración</Heading>
          <div className="flex flex-col gap-4">
            <SelectField
              key={`user-type-${type || "empty"}`}
              label="Tipo de usuario"
              placeholder="Seleccione tipo de usuario"
              name="type"
              required
              value={type || ""}
              options={USER_TYPES}
              onChange={(value) => {
                setValue(
                  "type",
                  value as
                    | "APPLICANT"
                    | "FACILITATOR"
                    | "REPRESENTATIVE"
                    | "INSTITUTION",
                );
                trigger("type");
                const userType = USER_TYPES.find((t) => t.value === value);
                setSelectedUserType(userType ?? null);
              }}
              isLoading={false}
            />
          </div>
        </div>
      </form>
    </>
  );
};

export default ExternalUserForm;
